import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { createCheckoutSession } from "@/services/payments";
import { getMetaAttribution } from "@/utils/metaAttribution";
import { Brain, Award, Mail, LayoutGrid, UserCircle, Search } from "lucide-react";

const MONTHLY_PRICE_ID =
  import.meta.env.VITE_STRIPE_PRICE_ID_MONTHLY || "price_monthly_placeholder";
const YEARLY_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_ID_YEARLY || "price_yearly_placeholder";

interface PartnerPromoDisplay {
  callout: string;
  monthlyAfter: string;
  yearlyAfter: string;
}

const PARTNER_PROMO_DISPLAY: Record<string, PartnerPromoDisplay> = {
  herrontour: {
    callout: "Herron Tour discount applied automatically.",
    monthlyAfter: "$32/mo after trial",
    yearlyAfter: "$320/yr after trial",
  },
};

const features = [
  {
    icon: Brain,
    title: "Personalized Recruiting Coach",
    description:
      "Guidance tailored to your student 24/7 — from NCAA rules for your class year to which tournaments to prioritize",
  },
  {
    icon: Award,
    title: "School Matching on Real Data",
    description:
      "Current Fit and Projected Fit based on historical commit profiles. See where your student actually has a chance",
  },
  {
    icon: Mail,
    title: "Coach Outreach with Tracking",
    description:
      "Personalized emails to coaches with your student's real stats. Track opens and clicks to see which coaches are engaging",
  },
  {
    icon: LayoutGrid,
    title: "My Schools Pipeline",
    description:
      "Track every school on a visual board — Researching → Reached Out → In Conversation → Visited → Offered",
  },
  {
    icon: UserCircle,
    title: "Guided Player Profile",
    description:
      "Strength meter shows what to add and why. Every field has context — no blank forms",
  },
  {
    icon: Search,
    title: "2,000+ Programs",
    description:
      "Search with 15+ filters: scoring range, net price, acceptance rate, majors, coach email availability, and more",
  },
];

const Subscribe = () => {
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [loadingPlan, setLoadingPlan] = useState<"monthly" | "yearly" | null>(null);
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref") ?? undefined;
  const promoDisplay = ref ? (PARTNER_PROMO_DISPLAY[ref] ?? null) : null;

  const trialEndDate = promoDisplay
    ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
      })
    : null;

  function validateEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  async function handleSubscribe(plan: "monthly" | "yearly") {
    if (!email) {
      toast({
        title: "Email required",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }
    if (!validateEmail(email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }
    if (email.toLowerCase() !== confirmEmail.toLowerCase()) {
      toast({
        title: "Emails don't match",
        description: "Please make sure both email fields match.",
        variant: "destructive",
      });
      return;
    }

    const priceId = plan === "monthly" ? MONTHLY_PRICE_ID : YEARLY_PRICE_ID;

    try {
      setLoadingPlan(plan);
      const { url } = await createCheckoutSession(email, priceId, ref, getMetaAttribution());
      window.location.href = url;
    } catch (err: any) {
      toast({
        title: "Something went wrong",
        description: String(err?.message || "Failed to start checkout"),
        variant: "destructive",
      });
    } finally {
      setLoadingPlan(null);
    }
  }

  return (
    <div className="min-h-screen w-full bg-background">
      {/* Hero + Pricing — above the fold */}
      <section className="px-4 pt-16 pb-12 max-w-2xl mx-auto space-y-8">
        <div className="text-center space-y-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary-text">
            College Golf Recruiting, Reimagined
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
            Your student's college golf matches — based on who each program{" "}
            <span className="text-primary-text">actually recruits.</span>
          </h1>
          <p className="text-muted-foreground text-lg">
            A personalized recruiting plan built on real commitment data from 2,000+ programs.
          </p>
        </div>

        {/* Checkout card — email + plan selection grouped */}
        <Card className="p-6 space-y-6">
          {promoDisplay && trialEndDate && (
            <div className="rounded-md bg-primary/10 border border-primary/20 px-4 py-3 text-sm space-y-1">
              <p className="font-semibold text-primary-text">
                Start with 7 days free — no charge until {trialEndDate}.
              </p>
              <p className="text-muted-foreground">{promoDisplay.callout}</p>
            </div>
          )}
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium mb-1 block">Your email address</label>
              <Input
                type="email"
                name="email"
                autoComplete="email"
                placeholder="email@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Confirm email address</label>
              <Input
                type="email"
                name="confirm-email"
                autoComplete="email"
                placeholder="email@example.com"
                value={confirmEmail}
                onChange={e => setConfirmEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="border-t pt-6">
            <p className="text-sm font-medium mb-4 text-center">Choose your plan</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Monthly */}
              <div className="rounded-lg border p-4 text-center space-y-3">
                <p className="text-sm font-medium text-muted-foreground">Monthly</p>
                <div>
                  <span className="text-3xl font-bold">$40</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                {promoDisplay && (
                  <p className="text-xs text-muted-foreground">{promoDisplay.monthlyAfter}</p>
                )}
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => handleSubscribe("monthly")}
                  disabled={loadingPlan !== null}
                >
                  {loadingPlan === "monthly"
                    ? "Redirecting..."
                    : promoDisplay
                      ? "Start your free trial"
                      : "Get your matches"}
                </Button>
              </div>

              {/* Yearly */}
              <div className="rounded-lg border border-primary p-4 text-center space-y-3 relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="text-xs">Save $80</Badge>
                </div>
                <p className="text-sm font-medium text-muted-foreground">Yearly</p>
                <div>
                  <span className="text-3xl font-bold">$400</span>
                  <span className="text-muted-foreground">/year</span>
                </div>
                {promoDisplay && (
                  <p className="text-xs text-muted-foreground">{promoDisplay.yearlyAfter}</p>
                )}
                <Button
                  className="w-full"
                  onClick={() => handleSubscribe("yearly")}
                  disabled={loadingPlan !== null}
                >
                  {loadingPlan === "yearly"
                    ? "Redirecting..."
                    : promoDisplay
                      ? "Start your free trial"
                      : "Get your matches"}
                </Button>
              </div>
            </div>
          </div>

          <p className="text-center text-sm text-muted-foreground">
            About $1/day. Compare to recruiting consultants at $150+/hr.
          </p>
        </Card>
      </section>

      {/* The Gap — for scrollers who need convincing */}
      <section className="px-4 py-12 max-w-2xl mx-auto text-center space-y-4">
        <p className="text-lg sm:text-xl font-medium">
          You searched. You filtered. You got a list of 200 programs.{" "}
          <span className="font-bold">And you still had no idea which ones were realistic.</span>
        </p>
        <p className="text-muted-foreground">
          The problem isn't access to information.{" "}
          <span className="font-semibold text-foreground">
            It's that no one tells you where your student actually fits.
          </span>
        </p>
      </section>

      {/* Features */}
      <section className="px-4 py-12 max-w-4xl mx-auto space-y-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-center">
          Everything you need to <span className="text-primary-text">recruit with confidence.</span>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {features.map(feature => (
            <div key={feature.title} className="flex gap-4 p-4 rounded-lg border bg-card">
              <div className="shrink-0 mt-1">
                <feature.icon className="h-6 w-6 text-primary-text" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Data Credibility */}
      <section className="px-4 py-12 max-w-2xl mx-auto text-center">
        <p className="text-lg sm:text-xl font-medium">
          Not what coaches say they want. <span className="font-bold">What they actually do.</span>
        </p>
      </section>

      {/* Final CTA */}
      <section className="px-4 py-12 max-w-2xl mx-auto text-center">
        <p className="text-xl sm:text-2xl font-medium">
          Stop searching. <span className="font-bold text-primary-text">Start matching.</span>
        </p>
      </section>

      {/* Footer */}
      <footer className="px-4 pb-12 text-center text-sm text-muted-foreground">
        Already have an account?{" "}
        <Link to="/" className="text-primary-text hover:underline font-medium">
          Sign in
        </Link>
      </footer>
    </div>
  );
};

export default Subscribe;
